import { useEffect, useState } from "react";
import { Clock, Users, MapPin, Check, MessageCircle } from "lucide-react";
import { useLanguage, pick, waLink } from "@/i18n";
import { api } from "@/api";
import { useSEO } from "@/hooks/useSEO";
import SectionHeader from "@/components/SectionHeader";

const HERO_IMG = "/images/capri-faraglioni.jpeg";

export default function Boat() {
  const { lang, t } = useLanguage();
  useSEO(t("seo_boat_title"), t("seo_boat_desc"));
  const [experiences, setExperiences] = useState([]);

  useEffect(() => {
    api.experiences({ category: "sea" }).then(setExperiences).catch(() => {});
  }, []);

  return (
    <div data-testid="boat-page">
      <section className="relative h-[75vh] min-h-[500px] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Capri e le Isole" className="absolute inset-0 w-full h-full object-cover" />
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_IMG}
          data-testid="boat-hero-video"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://videos.pexels.com/video-files/2169880/2169880-hd_1280_720_30fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-medblue/45" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-20 w-full">
          <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-ivory/80 mb-4">Capri · Ischia · Procida</p>
          <h1 className="font-serif text-4xl md:text-6xl text-ivory tracking-tight leading-[1.05] max-w-3xl" data-testid="boat-hero-title">
            {t("nav_boat")}
          </h1>
          <p className="mt-4 text-ivory/80 text-xs uppercase tracking-[0.2em]" data-testid="boat-hero-note">{t("boat_hero_note")}</p>
          <div className="flex flex-wrap gap-4 mt-8">
            <a href="#boat-list" data-testid="boat-discover-cta"
              className="inline-flex items-center gap-2 bg-ivory text-ink text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-terracotta transition-colors duration-300">
              {t("discover_experience")}
            </a>
            <a href={waLink(t("wa_message") + " Capri e le Isole")} target="_blank" rel="noopener noreferrer" data-testid="boat-book-cta"
              className="inline-flex items-center gap-2 border border-ivory/60 text-ivory text-xs font-medium uppercase tracking-[0.2em] px-7 py-4 hover:bg-ivory hover:text-ink transition-colors duration-300">
              {t("book_now")}
            </a>
          </div>
        </div>
      </section>

      <section id="boat-list" className="py-24 bg-ivory" data-testid="boat-experiences-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader overline="Capri · Ischia · Procida" title={t("nav_boat")} sub={t("boat_page_sub")} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            {experiences.map((exp) => (
              <div key={exp.slug} className="border border-stone2/60 bg-ivory flex flex-col" data-testid={`boat-card-${exp.slug}`}>
                <div className="relative overflow-hidden aspect-[16/9] img-zoom">
                  <img src={exp.image} alt={pick(exp.title, lang)} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    {exp.badges.map((b) => (
                      <span key={b} className="bg-ivory/90 text-ink text-[10px] font-medium uppercase tracking-[0.15em] px-3 py-1.5">
                        {t(`badge_${b.replace(/ /g, "_")}`)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="p-8 flex flex-col flex-1">
                  <h3 className="font-serif text-3xl text-ink">{pick(exp.title, lang)}</h3>
                  <p className="mt-3 text-sm text-ink/60 leading-relaxed">{pick(exp.short, lang)}</p>
                  <div className="grid grid-cols-2 gap-4 mt-6 text-xs text-ink/60">
                    <span className="flex items-center gap-2"><Clock size={13} strokeWidth={1.5} className="text-medblue" /> {pick(exp.duration, lang)}</span>
                    <span className="flex items-center gap-2"><Users size={13} strokeWidth={1.5} className="text-medblue" /> {pick(exp.group_size, lang)}</span>
                    <span className="flex items-center gap-2 col-span-2"><MapPin size={13} strokeWidth={1.5} className="text-medblue" /> {t("boat_departure")}: {pick(exp.meeting_point, lang).split("—")[0]}</span>
                  </div>
                  <div className="mt-6 pt-5 border-t border-stone2/50">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ink/40 mb-3">{t("whats_included")}</p>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                      {exp.included.slice(0, 3).map((inc, i) => (
                        <span key={i} className="flex items-center gap-1.5 text-xs text-ink/60">
                          <Check size={12} strokeWidth={1.5} className="text-sage" /> {pick(inc, lang)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <a
                    href={waLink(`${t("wa_book_prefix")} ${pick(exp.title, lang)}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`boat-book-${exp.slug}`}
                    className="mt-8 inline-flex items-center justify-center gap-2 bg-medblue text-ivory text-xs font-medium uppercase tracking-[0.2em] px-6 py-4 hover:bg-ink transition-colors duration-300"
                  >
                    <MessageCircle size={14} strokeWidth={1.5} /> {t("book_boat")}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-medblue py-24" data-testid="boat-cta-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-ivory tracking-tight">{t("boat_cta_title")}</h2>
          <p className="mt-5 text-ivory/70 text-base md:text-lg">{t("boat_cta_sub")}</p>
          <a
            href={waLink(t("wa_message") + " Capri e le Isole")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="boat-final-cta"
            className="inline-flex items-center gap-2 mt-10 bg-terracotta text-ink text-xs font-medium uppercase tracking-[0.2em] px-8 py-4 hover:bg-ivory transition-colors duration-300"
          >
            <MessageCircle size={14} strokeWidth={1.5} /> {t("book_boat")}
          </a>
        </div>
      </section>
    </div>
  );
}
